// proceduralLibrary/ThemeStrip.jsx
//
// The recolor UI ported from the deck THEME_STRIP (notes5:193-235). Shows the 10
// curated palette chips + "Segment" / "Wheel" auto-match pills. Selecting a chip
// calls onSelect(paletteName, paletteObj); a match pill calls
// onSelect(null, derivedTriple, "segment"|"wheel") — the caller uses that 3rd
// arg (`selectedMatch` prop, below) so the strip reflects which one is truly
// active instead of leaving a stale named chip highlighted after a match-pill
// recolor. Motion-free.

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { THEME_PALETTES } from "./designs/designGenerators";
import { colors } from "../../utils/theme";
import { fontFamily } from "../../theme/tokens";

// Derive a 3-tone palette {light, deep, accent} from a single base hex, so the
// Segment / Wheel match pills can recolor a design/loop to the live color.
const clampByte = (n) => Math.max(0, Math.min(255, Math.round(n)));
const toHex = (n) => clampByte(n).toString(16).padStart(2, "0");
const parseHex = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
};
const mix = (rgb, target, t) => rgb.map((v, i) => v + (target[i] - v) * t);
const deriveTriple = (hex) => {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const light = mix(rgb, [255, 255, 255], 0.35);
  const deep = mix(rgb, [0, 0, 0], 0.18);
  const accent = mix(rgb, [255, 255, 255], 0.82);
  const fmt = (a) => `#${toHex(a[0])}${toHex(a[1])}${toHex(a[2])}`;
  return { light: fmt(light), deep: fmt(deep), accent: fmt(accent) };
};

const Pill = ({ label, title, onClick, selected }) => (
  <Box
    component="button"
    type="button"
    title={title}
    aria-pressed={selected}
    onClick={onClick}
    sx={{
      height: 32,
      minWidth: 44,
      px: 1.25,
      border: selected ? `1px solid ${colors.PrimaryPurple}` : `1px solid ${colors.lightBorder}`,
      boxShadow: selected ? `0 0 0 2px ${colors.PrimaryPurple}59` : "none",
      borderRadius: "999px",
      background: colors.white,
      fontFamily,
      fontSize: "12px",
      fontWeight: 600,
      color: selected ? colors.PrimaryPurple700 : colors.darkGrey,
      cursor: "pointer",
      whiteSpace: "nowrap",
      // #C9A8FF has no matching design token yet — kept literal.
      "&:hover": { borderColor: "#C9A8FF", color: colors.PrimaryPurple700 },
      "&:focus-visible": { outline: "none", boxShadow: `0 0 0 2px ${colors.lightPurple}` },
    }}
  >
    {label}
  </Box>
);

const ThemeStrip = ({
  label = "Color theme",
  selectedName,
  // Which match pill (if any) produced the CURRENT recolor — "segment" |
  // "wheel" | null. Lets the strip show the truth after a match-pill recolor
  // instead of leaving a stale named chip highlighted (it never matches a
  // derived triple, since derived triples aren't one of the 10 curated names).
  selectedMatch = null,
  onSelect,
  segmentColor,
  wheelColor,
}) => {
  const applyDerived = (hex, source) => {
    const t = deriveTriple(hex);
    if (t && onSelect) onSelect(null, t, source);
  };
  return (
    <Box
      sx={{
        mt: 1.25,
        background: colors.white,
        border: `1px solid ${colors.lightBorder}`,
        borderRadius: "10px",
        p: 1.25,
      }}
    >
      <Typography
        sx={{ fontSize: "12px", fontWeight: 700, color: colors.primary, mb: 1, fontFamily }}
      >
        {label}
      </Typography>

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1, flexWrap: "wrap", gap: 0.75 }}>
        {segmentColor && (
          <Pill
            label="Segment"
            title="Match the Segment color"
            selected={selectedMatch === "segment"}
            onClick={() => applyDerived(segmentColor, "segment")}
          />
        )}
        {wheelColor && (
          <Pill
            label="Wheel"
            title="Match the Wheel theme"
            selected={selectedMatch === "wheel"}
            onClick={() => applyDerived(wheelColor, "wheel")}
          />
        )}
        {/* #9A9AA0 has no matching design token yet — kept literal. */}
        <Typography sx={{ fontSize: "11px", color: "#9A9AA0", fontFamily }}>
          auto-match, or pick a palette:
        </Typography>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 0.75 }}>
        {THEME_PALETTES.map((p) => {
          const on = p.name === selectedName;
          return (
            <Box
              key={p.name}
              role="button"
              tabIndex={0}
              aria-label={`${p.name} theme`}
              aria-pressed={on}
              title={`${p.name} theme`}
              onClick={() => onSelect && onSelect(p.name, p)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect && onSelect(p.name, p);
                }
              }}
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                cursor: "pointer",
                // rgba derived from colors.primary #123051 at 12% — no exact
                // token for this alpha step yet, kept literal.
                border: "1px solid rgba(18,48,81,.12)",
                background: `linear-gradient(135deg, ${p.light} 0 50%, ${p.deep} 50% 100%)`,
                boxShadow: on ? `0 0 0 2px ${colors.white}, 0 0 0 4px ${colors.PrimaryPurple}` : "none",
                // #E9DBFC has no matching design token yet — kept literal.
                "&:hover": { boxShadow: "0 0 0 2px #E9DBFC" },
                "&:focus-visible": { outline: "none", boxShadow: `0 0 0 2px ${colors.lightPurple}` },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ThemeStrip;
